import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Github, Linkedin, Mail, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { profile } from '../data/profile';
import { featuredProjects } from '../data/projects';
import useMediaQuery from '../hooks/useMediaQuery';
import useTypewriter from '../hooks/useTypewriter';
import Magnetic from './Magnetic';
import Spotlight from './Spotlight';
import TextReveal from './TextReveal';
import XIcon from './XIcon';
import './Hero.css';

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const winList = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};
const winItem = {
  hidden: { opacity: 0, x: -10 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

const Hero = ({ onContactClick }) => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const reducedMotion = useReducedMotion();
  const parallaxOn = !isMobile && !reducedMotion;

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const orb1Y = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const orb2Y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const gridOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const { output: typedPrompt, done: typedDone } = useTypewriter('ls ./wins --award', {
    cps: 24,
    startDelay: 1000,
    enabled: !reducedMotion,
  });

  return (
    <section className="hero" id="top" ref={heroRef}>
      <motion.div
        className="hero__grid"
        aria-hidden="true"
        style={parallaxOn ? { opacity: gridOpacity } : undefined}
      />
      <motion.div
        className="hero__orb-wrap hero__orb-wrap--1"
        aria-hidden="true"
        style={parallaxOn ? { y: orb1Y } : undefined}
      >
        <div className="hero__orb hero__orb--1" />
      </motion.div>
      <motion.div
        className="hero__orb-wrap hero__orb-wrap--2"
        aria-hidden="true"
        style={parallaxOn ? { y: orb2Y } : undefined}
      >
        <div className="hero__orb hero__orb--2" />
      </motion.div>
      <div className="hero__conic" aria-hidden="true" />
      <Spotlight containerRef={heroRef} />

      <div className="container hero__inner">
        <motion.div
          className="hero__content"
          variants={container}
          initial="hidden"
          animate="show"
          style={parallaxOn ? { y: contentY } : undefined}
        >
          <motion.div className="hero__status" variants={item}>
            <span className="hero__status-dot" />
            {profile.role} · {profile.location}
          </motion.div>

          <TextReveal as="h1" className="hero__title" text={profile.name} mode="chars" delay={0.25} />

          <TextReveal
            as="p"
            className="hero__tagline gradient-text gradient-text--animated"
            text={profile.tagline}
            mode="words"
            delay={0.55}
          />

          <motion.p className="hero__intro" variants={item}>
            {profile.intro}
          </motion.p>

          <motion.div className="hero__currents" variants={item}>
            {profile.currents.map((c) => (
              <a
                key={c.name}
                className={`hero__current ${c.highlight ? 'hero__current--highlight' : ''}`}
                href={c.url}
                target="_blank"
                rel="noreferrer"
              >
                <span className="hero__current-dot" />
                {c.label} <strong>{c.name}</strong>
                {c.live && <span className="hero__current-live">Live</span>}
                <ArrowUpRight size={15} />
              </a>
            ))}
          </motion.div>

          <motion.div className="hero__actions" variants={item}>
            <Magnetic strength={0.3}>
              <button className="btn btn-primary" onClick={() => navigate('/projects')}>
                View work <ArrowRight size={18} />
              </button>
            </Magnetic>
            <Magnetic strength={0.3}>
              <button className="btn btn-ghost" onClick={onContactClick}>
                Get in touch
              </button>
            </Magnetic>
          </motion.div>

          <motion.div className="hero__socials" variants={item}>
            <Magnetic strength={0.2}>
              <a href={profile.socials.github} target="_blank" rel="noreferrer" aria-label="GitHub"><Github size={20} /></a>
            </Magnetic>
            <Magnetic strength={0.2}>
              <a href={profile.socials.x} target="_blank" rel="noreferrer" aria-label="X (Twitter)"><XIcon size={18} /></a>
            </Magnetic>
            <Magnetic strength={0.2}>
              <a href={profile.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin size={20} /></a>
            </Magnetic>
            <Magnetic strength={0.2}>
              <a href={profile.socials.email} aria-label="Email"><Mail size={20} /></a>
            </Magnetic>
            <Magnetic strength={0.2}>
              <a href={profile.socials.devpost} target="_blank" rel="noreferrer" className="hero__devpost">Devpost</a>
            </Magnetic>
          </motion.div>
        </motion.div>

        <motion.aside
          className="hero__panel glass"
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
        >
          <div className="hero__panel-bar">
            <span /><span /><span />
            <code>~/wins</code>
          </div>
          <div className="hero__panel-body">
            <div className="hero__prompt" aria-hidden="true">
              <span className="hero__prompt-sign">$</span> {typedPrompt}
              <span className={`hero__caret ${typedDone ? 'hero__caret--idle' : ''}`} />
            </div>
            <div className="hero__panel-label">Award-winning builds</div>
            <motion.div
              className="hero__wins"
              variants={winList}
              initial={reducedMotion ? false : 'hidden'}
              animate={typedDone ? 'show' : 'hidden'}
            >
              {featuredProjects.filter((p) => p.award).map((p) => (
                <motion.button
                  key={p.id}
                  className="hero__win"
                  variants={winItem}
                  onClick={() => navigate(`/project/${p.id}`)}
                >
                  <Trophy size={15} className="hero__win-icon" />
                  <span className="hero__win-name">{p.title}</span>
                  <span className="hero__win-event">{p.hackathon}</span>
                </motion.button>
              ))}
            </motion.div>
          </div>
        </motion.aside>
      </div>
    </section>
  );
};

export default Hero;
